// @ts-nocheck
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import translate from "@insite/client-framework/Translate";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import cancelDraftFieldValues from "@store/Framework/Store/Greif/Components/PackagingAdviceForm/Handlers/CancelDraftFieldValues";
import saveDraftFieldValues from "@store/Framework/Store/Greif/Components/PackagingAdviceForm/Handlers/SaveDraftFieldValues";
import setDraftFieldValues from "@store/Framework/Store/Greif/Components/PackagingAdviceForm/Handlers/SetDraftFieldValues";
import { useStepperContextStep } from "@store/Framework/Components/StepperContext";
import { PackagingAdviceFormFieldValues } from "@store/Framework/Store/Greif/Components/PackagingAdviceForm/PackagingAdviceFormState";
import FormStep, { FormStepPresentationProps, FormStepProps } from "@store/Library/Components/FormStepper/FormStep";
import TextAreaFormField, {
    TextAreaFormFieldPresentationProps,
} from "@store/Library/Components/FormField/TextAreaFormField";
import { formStepProps } from "@store/Themes/common/props/formStep";
import React, { FC, useState } from "react";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { css } from "styled-components";
import siteMessage from "@insite/client-framework/SiteMessage";
import FileUploadFormField, {
    FileUploadFormFieldPresentationProps,
} from "@store/Library/Components/FormField/FileUploadFormField";
import constants from "@store/constants";
import FormFieldUploadedFile from "@store/Library/Components/FormField/FormFieldUploadedFile";

const stepFieldValueKeys: Array<keyof PackagingAdviceFormFieldValues> = ["attachments", "requestDetails"];

export interface PackagingAdviceFormDetailsStepProps extends Partial<Omit<FormStepProps, "contextStepNumber">> {}

export interface PackagingAdviceFormDetailsStepStyles {
    attachmentsInfoValue?: TypographyPresentationProps;
    fileUploadFormField?: FileUploadFormFieldPresentationProps;
    formGridContainer?: GridContainerProps;
    formGridItem?: GridItemProps;
    formStep?: FormStepPresentationProps;
    infoGridContainer?: GridContainerProps;
    infoGridItem?: GridItemProps;
    infoLabel?: TypographyPresentationProps;
    infoValue?: TypographyPresentationProps;
    requestDetailsFormField?: TextAreaFormFieldPresentationProps;
}

export const packagingAdviceFormDetailsStepStyles: PackagingAdviceFormDetailsStepStyles = {
    formGridContainer: formStepProps.formGridContainer,
    formGridItem: {
        ...formStepProps.formGridItem,
        width: [12, 12, 6, 6, 6],
    },
    infoGridContainer: formStepProps.infoGridContainer,
    infoGridItem: formStepProps.infoGridItem,
    infoLabel: formStepProps.infoLabel,
    infoValue: formStepProps.infoValue,
    attachmentsInfoValue: mergeProps<TypographyPresentationProps>(formStepProps.infoValue, {
        forwardAs: "div",
        css: css`
            width: 100%;
        `,
    }),
};

const styles = packagingAdviceFormDetailsStepStyles;

const PackagingAdviceFormDetailsStep: FC<PackagingAdviceFormDetailsStepProps> = ({ ...formStepProps }) => {
    const dispatch = useAppDispatch();
    const [showErrors, setShowErrors] = useState(false);
    const { isComplete, stepNumber } = useStepperContextStep();
    const draftFieldValues = useAppSelector(state => state.greif.components.packagingAdviceForm.formDraftFieldValues);
    const fieldValues = useAppSelector(state => state.greif.components.packagingAdviceForm.formFieldValues);

    if (!stepNumber) {
        return null;
    }

    const attachmentsLabel = translate("Product SDS and Current Packaging Information");
    const requestDetailsLabel = translate("Request Details");
    const requestDetailsErrorMessage = siteMessage("PackagingAdviceForm_RequestDetailsRequired").toString();
    const savedAttachments = fieldValues.attachments.value;

    return (
        <FormStep
            {...styles.formStep}
            contextStepNumber={stepNumber}
            onCancel={() => dispatch(cancelDraftFieldValues(stepFieldValueKeys))}
            onSubmit={event => {
                if (!showErrors) {
                    setShowErrors(true);
                }

                if (!stepFieldValueKeys.every(x => draftFieldValues[x].isValid !== false)) {
                    event.preventDefault();
                    return;
                }

                dispatch(saveDraftFieldValues(stepFieldValueKeys));
            }}
            title={translate("Details")}
            {...formStepProps}
        >
            {isComplete ? (
                <GridContainer {...styles.infoGridContainer}>
                    <GridItem {...styles.infoGridItem}>
                        <Typography {...styles.infoLabel}>{attachmentsLabel}</Typography>
                        <Typography {...styles.attachmentsInfoValue}>
                            {savedAttachments.length
                                ? savedAttachments.map((attachment, index) => (
                                      <FormFieldUploadedFile
                                          file={attachment}
                                          key={`${attachment.fileName}-${index}`}
                                      />
                                  ))
                                : "—"}
                        </Typography>
                    </GridItem>
                    <GridItem {...styles.infoGridItem} width={12}>
                        <Typography {...styles.infoLabel}>{requestDetailsLabel}</Typography>
                        <Typography {...styles.infoValue}>{fieldValues.requestDetails.value || "—"}</Typography>
                    </GridItem>
                </GridContainer>
            ) : (
                <GridContainer {...styles.formGridContainer}>
                    <GridItem {...styles.formGridItem}>
                        <FileUploadFormField
                            {...styles.fileUploadFormField}
                            accept=".pdf,.zip"
                            defaultValue={draftFieldValues.attachments.value}
                            label={attachmentsLabel}
                            multiple
                            onUpdate={attachments => dispatch(setDraftFieldValues({ attachments }))}
                            required
                            requiredErrorMessage={siteMessage("PackagingAdviceForm_AttachmentRequired").toString()}
                            showError={showErrors}
                        />
                    </GridItem>
                    <GridItem {...styles.formGridItem} width={12}>
                        <TextAreaFormField
                            {...styles.requestDetailsFormField}
                            defaultValue={draftFieldValues.requestDetails.value}
                            label={requestDetailsLabel}
                            labelDescription={siteMessage("PackagingAdviceForm_RequestDetailsDescription").toString()}
                            minLength={10}
                            minLengthErrorMessage={requestDetailsErrorMessage}
                            onUpdate={requestDetails => dispatch(setDraftFieldValues({ requestDetails }))}
                            placeholder={siteMessage("PackagingAdviceForm_RequestDetailsPlaceholder").toString()}
                            required
                            requiredErrorMessage={requestDetailsErrorMessage}
                            showError={showErrors}
                        />
                    </GridItem>
                </GridContainer>
            )}
        </FormStep>
    );
};

export default PackagingAdviceFormDetailsStep;
